import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { maxPublicBatchAlYear } from "@/lib/publicBatchVisibility";
import { formatNewsMonthYear } from "@/lib/displayOrderDates";
import { clearSharePageMeta, setProjectShareMeta } from "@/lib/socialMeta";
import { normalizeExternalHref } from "@/lib/entitySocialLinks";

type BranchRef = { name: string; slug: string } | null;
type BatchRef = {
  slug: string;
  custom_title: string | null;
  official_title: string | null;
  al_year: number | null;
} | null;
type AssociationRef = { name: string; slug: string } | null;

type ProjectRow = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
  created_at: string | null;
  updated_at: string | null;
  branch: BranchRef;
  batch: BatchRef;
  association: AssociationRef;
};

function one<T>(x: T | T[] | null | undefined): T | null {
  if (x == null) return null;
  return Array.isArray(x) ? (x[0] ?? null) : x;
}

function batchLabel(b: NonNullable<BatchRef>) {
  return b.custom_title || b.official_title || "Batch";
}

function isObaProject(p: ProjectRow) {
  return !p.branch && !p.batch && !p.association;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function ProjectDetailPage() {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState<ProjectRow | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    clearSharePageMeta();
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !UUID_RE.test(projectId)) {
      setRow(null);
      setNotFound(true);
      setLoading(false);
      return;
    }
    setRow(null);
    setLoading(true);
    setNotFound(false);
    void supabase
      .from("projects")
      .select(
        `
        id,
        title,
        description,
        image_url,
        link_url,
        created_at,
        updated_at,
        branch:branches(name, slug),
        batch:batches(slug, custom_title, official_title, al_year),
        association:associations(name, slug)
      `
      )
      .eq("id", projectId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) {
          setRow(null);
          setNotFound(true);
          setLoading(false);
          return;
        }
        const d = data as {
          id: string;
          title: string;
          description: string | null;
          image_url: string | null;
          link_url: string | null;
          created_at: string | null;
          updated_at: string | null;
          branch: BranchRef | BranchRef[];
          batch: BatchRef | BatchRef[];
          association: AssociationRef | AssociationRef[];
        };
        const normalized: ProjectRow = {
          id: d.id,
          title: d.title,
          description: d.description,
          image_url: d.image_url,
          link_url: d.link_url,
          created_at: d.created_at,
          updated_at: d.updated_at,
          branch: one(d.branch),
          batch: one(d.batch),
          association: one(d.association),
        };

        const cutoff = maxPublicBatchAlYear();
        const b = normalized.batch;
        if (b?.al_year != null && b.al_year > cutoff) {
          setRow(null);
          setNotFound(true);
          setLoading(false);
          return;
        }

        setRow(normalized);
        setLoading(false);
      });
  }, [projectId]);

  useEffect(() => {
    if (!row) return;
    const path = `/projects/${row.id}`;
    setProjectShareMeta({
      title: row.title,
      description: row.description,
      imageUrl: row.image_url,
      canonicalPath: path,
    });
    return () => {
      clearSharePageMeta();
    };
  }, [row]);

  if (!projectId) return null;

  if (!loading && notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center text-brand-blue/70">
        Project not found.
        <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-2">
          <Link to="/projects" className="text-brand-maroon font-semibold">
            ← OBA Projects
          </Link>
          <span className="text-brand-blue/30">|</span>
          <Link to="/projects/other" className="text-brand-maroon font-semibold">
            Affiliated Projects →
          </Link>
        </div>
      </div>
    );
  }

  const safeLink = row ? normalizeExternalHref(row.link_url) : null;
  const backHref = row ? (isObaProject(row) ? "/projects" : "/projects/other") : "/projects";
  const backLabel = row ? (isObaProject(row) ? "OBA Projects" : "Affiliated Projects") : "Projects";
  const monthLine = row ? formatNewsMonthYear(row.updated_at ?? row.created_at) : null;

  return (
    <div className="mx-auto max-w-4xl px-4 py-14">
      <Link to={backHref} className="text-sm font-semibold text-brand-maroon hover:underline">
        ← {backLabel}
      </Link>

      {loading ? (
        <p className="mt-8 text-brand-blue/60">Loading…</p>
      ) : row ? (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
            {row.branch ? (
              <Link
                to={`/alumni/branches/${row.branch.slug}`}
                className="text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
              >
                {row.branch.name}
              </Link>
            ) : null}
            {row.batch ? (
              <Link
                to={`/alumni/batches/${row.batch.slug}`}
                className="text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
              >
                {batchLabel(row.batch)}
              </Link>
            ) : null}
            {row.association ? (
              <Link
                to={`/alumni/associations/${row.association.slug}`}
                className="text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
              >
                {row.association.name}
              </Link>
            ) : null}
            {!row.branch && !row.batch && !row.association ? (
              <span className="text-xs font-bold uppercase tracking-wide text-brand-maroon">OBA</span>
            ) : null}
            {monthLine ? (
              <time
                dateTime={row.updated_at ?? row.created_at ?? undefined}
                className="text-xs font-semibold uppercase tracking-wide text-brand-maroon/80"
              >
                {monthLine}
              </time>
            ) : null}
          </div>

          <h1 className="mt-4 font-display text-4xl font-bold text-brand-blue">{row.title}</h1>

          {row.image_url ? (
            <img
              src={row.image_url}
              alt=""
              className="mt-8 w-full max-h-[520px] rounded-2xl object-cover"
            />
          ) : null}

          {row.description ? (
            <div className="prose prose-blue mt-8 max-w-none">
              <p className="whitespace-pre-line text-brand-blue/90 leading-relaxed">{row.description}</p>
            </div>
          ) : (
            <p className="mt-8 text-brand-blue/70">No description provided.</p>
          )}

          {safeLink ? (
            <p className="mt-10">
              <a
                href={safeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex rounded-full bg-brand-maroon px-6 py-3 text-sm font-bold uppercase text-white hover:opacity-95"
              >
                Open project link →
              </a>
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
